import { chargedUsageKindIds, USAGE_KINDS } from './usageKinds';

/**
 * Pure helpers for the AI usage admin view.
 *
 * The API returns one line per (kind, business, user, model) with tokens and
 * an estimated cost. Everything the screen shows is a regrouping of those
 * lines, done here so it can be tested without React.
 *
 * The kinds are the registry's (`usageKinds.js`): each gets its totals and a
 * company table under `kinds[id]`, and a row in `byKind`, the feature
 * overview. The company and user tables at the top of the page span every
 * kind, each row carrying its `kinds` — which kinds it used, with their
 * counts — since a sum of chat turns and images tagged is a number that
 * needs its parts. People are counted only from the kinds whose rows carry
 * people (`byPeople`): a `SYSTEM:` actor is no user, so the user table has
 * no row for it and a company's users count leaves it out. The licence tab
 * reads `chargedByCompany`: the kinds the registry marks `charged`, because
 * those are the rows a licence or the free-tier allowance pays for.
 *
 * Cost sums distinguish "nothing priced" from "some of it unpriced": a line
 * whose model has no rate contributes `costUsd: null`, and a total built only
 * from such lines stays `null` rather than reading as a free month. A total
 * that mixes priced and unpriced lines keeps the priced sum and sets
 * `unpriced`, which the screen marks with an asterisk.
 */

export const toYearMonth = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const currentYearMonth = (now = new Date()) => toYearMonth(now);

const emptyTotals = () => ({
  turns: 0,
  modelCalls: 0,
  toolCalls: 0,
  inputTokens: 0,
  outputTokens: 0,
  cacheReadTokens: 0,
  cacheWriteTokens: 0,
  incompleteTurns: 0,
  // Null until a line carries the count: tagging from before the tagger kept
  // it, or an API build that does not send it, is unknown rather than none.
  tagsAdded: null,
  // The turns of the lines that carried it: the images a per-image average
  // of `tagsAdded` divides by.
  tagsAddedTurns: 0,
  costUsd: null,
  unpriced: false,
});

export const sumLines = (lines) =>
  lines.reduce((acc, line) => {
    acc.turns += line.turns || 0;
    acc.modelCalls += line.modelCalls || 0;
    acc.toolCalls += line.toolCalls || 0;
    acc.inputTokens += line.inputTokens || 0;
    acc.outputTokens += line.outputTokens || 0;
    acc.cacheReadTokens += line.cacheReadTokens || 0;
    acc.cacheWriteTokens += line.cacheWriteTokens || 0;
    acc.incompleteTurns += line.incompleteTurns || 0;
    if (line.tagsAdded != null) {
      acc.tagsAdded = (acc.tagsAdded || 0) + line.tagsAdded;
      acc.tagsAddedTurns += line.turns || 0;
    }
    if (line.costUsd == null) {
      acc.unpriced = true;
    } else {
      acc.costUsd = (acc.costUsd || 0) + line.costUsd;
    }
    return acc;
  }, emptyTotals());

export const totalTokens = (totals) =>
  totals.inputTokens +
  totals.outputTokens +
  totals.cacheReadTokens +
  totals.cacheWriteTokens;

const unique = (values) => Array.from(new Set(values));

const groupBy = (lines, keyOf, build) => {
  const groups = new Map();
  for (const line of lines) {
    const key = keyOf(line);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(line);
  }
  return Array.from(groups, ([key, group]) => build(key, group));
};

export const summariseReport = (report) => {
  const lines = report.lines || [];
  const businesses = report.businesses || {};
  const users = report.users || {};
  const rates = report.rates || {};

  const peopleKinds = new Set(
    USAGE_KINDS.filter((kind) => kind.byPeople).map((kind) => kind.id)
  );
  /** The people on a group's rows: the users of the kinds that have any. */
  const peopleOf = (group) =>
    unique(
      group
        .filter((line) => peopleKinds.has(line.kind))
        .map((line) => line.userId)
    );
  /** The kinds a group used, in the registry's order, each with its totals. */
  const kindCounts = (group) =>
    USAGE_KINDS.map((kind) => {
      const own = group.filter((line) => line.kind === kind.id);
      return own.length ? { kind: kind.id, ...sumLines(own) } : null;
    }).filter(Boolean);

  const companyRow = (key, group) => ({
    businessId: key || null,
    businessName: key ? businesses[key] : undefined,
    users: peopleOf(group).length,
    kinds: kindCounts(group),
    ...sumLines(group),
  });
  const byCompanyOf = (group) =>
    groupBy(group, (line) => line.businessId || '', companyRow);

  // Every kind the registry knows, used in this period or not: the overview
  // says "0" for a feature nobody used rather than leaving it out.
  const kinds = {};
  const byKind = USAGE_KINDS.map((kind) => {
    const group = lines.filter((line) => line.kind === kind.id);
    const byCompany = byCompanyOf(group);
    const totals = sumLines(group);
    const entry = {
      totals,
      byCompany,
      companies: byCompany.length,
      users: peopleOf(group).length,
    };
    kinds[kind.id] = entry;
    return {
      kind: kind.id,
      ...totals,
      companies: entry.companies,
      users: entry.users,
    };
  });

  const chargedKinds = new Set(chargedUsageKindIds());
  const charged = lines.filter((line) => chargedKinds.has(line.kind));

  return {
    all: sumLines(lines),
    kinds,
    byKind,
    byCompany: byCompanyOf(lines),
    chargedByCompany: byCompanyOf(charged),
    byUser: groupBy(
      lines.filter((line) => peopleKinds.has(line.kind)),
      (line) => line.userId,
      (key, group) => {
        const businessIds = unique(
          group.map((line) => line.businessId).filter(Boolean)
        );
        return {
          userId: key,
          userName: users[key],
          businessIds,
          businessNames: businessIds.map((id) => businesses[id] || id),
          kinds: kindCounts(group),
          ...sumLines(group),
        };
      }
    ),
    byModel: groupBy(
      lines,
      (line) => `${line.kind}|${line.modelId}`,
      (key, group) => ({
        key,
        kind: group[0].kind,
        modelId: group[0].modelId,
        modelLabel: rates[group[0].modelId]
          ? rates[group[0].modelId].label
          : undefined,
        ...sumLines(group),
      })
    ),
    unpricedModels: report.unpricedModels || [],
  };
};
